import './AccountPages.scss'
import React, { useEffect } from 'react'
import { useConnect } from 'redux-bundler-hook'
import { get } from 'lodash'
import Button from '@material-ui/core/Button'
import FormContainer from '@src/components/FormContainer'
import useTranslations from '@src/hooks/useTranslations'
import { azureApiGatewayPost } from '@src/api/azureApiGateway/client'
import { googleAnalytics } from '@src/api/googleanalytics/googleAnalytics'
import BEMHelper from 'react-bem-helper'
import { REGISTRATION_STEPS } from '@src/components/account/Account'

const bem = new BEMHelper('registration-page')

function ConfirmDriversLicenseAndCreditCheckPage({
	setNextStep,
	setError,
	setLoading,
}) {
	const t = useTranslations()
	const { customerInfo, businessCustomer } = useConnect(
		'selectCustomerInfo',
		'selectBusinessCustomer',
	)

	let pollTimeout = null
	let pollingTimedOutTimeout = null

	useEffect(() => {
		googleAnalytics.pushVirtualPageView(
			`/virtual/account/confirmdriverslicenseandcreditcheckpage/${customerInfo.customerId}`,
			`User with userid ${customerInfo.customerId} is confirming drivers license with credit check`,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		return () => {
			clearTimeout(pollTimeout)
			clearTimeout(pollingTimedOutTimeout)
		}
	}, [pollTimeout, pollingTimedOutTimeout])

	const verifyCustomer = async (transaction) => {
		const { transaction_id } = transaction
		setLoading(true)
		try {
			let customerInformation = {
				transaction_id,
				personal_number: customerInfo.sso,
				expiry_date: customerInfo.driverLicenseExpDate,
			}
			if (businessCustomer) {
				customerInformation.customer_type = 'business'
			}
			await azureApiGatewayPost(
				`customer/${customerInfo.customerId}/validation`,
				customerInformation,
				{ useToken: true, params: { registration: true } },
			)
			setNextStep(REGISTRATION_STEPS.SELECT_PAYMENT_METHOD)
		} catch (error) {
			setError({
				message: get(error, 'response.data.messages')
					? error.response.data.messages
					: 'kinto.error.failed-to-verify-customer',
			})
		}
		setLoading(false)
	}

	const pollBankId = async (transaction) => {
		if (pollTimeout) {
			clearTimeout(pollTimeout)
			pollTimeout = null
		}
		const { transaction_id } = transaction
		const resp = await azureApiGatewayPost(
			`customer/${customerInfo.customerId}/check_bankid`,
			{ transaction_id },
			{ useToken: true, params: { registration: true } },
		)
		if (resp.data && resp.data.verification) {
			if (resp.data.verification === 'failed') {
				setLoading(false)
				setError({ message: 'kinto.error.failed-to-verify-bankid' })
				clearTimeout(pollingTimedOutTimeout)
			} else if (resp.data.verification === 'complete') {
				clearTimeout(pollingTimedOutTimeout)
				setLoading(false)
				verifyCustomer(transaction)
			} else {
				pollTimeout = setTimeout(() => pollBankId(transaction), 5000)
			}
		}
	}

	const startBankIdPoll = (transaction) => {
		const { bankid_sign_timeout } = transaction
		setLoading(true)
		pollBankId(transaction)
		pollingTimedOutTimeout = setTimeout(() => {
			clearTimeout(pollTimeout)
			setLoading(false)
			setError({ message: 'kinto.error.bankid-timedout' })
		}, bankid_sign_timeout * 1000)
	}

	const initiateBank = async (onDevice = false) => {
		if (!customerInfo || !customerInfo.sso || customerInfo.sso === '') {
			setError({ message: 'kinto.error.no-sso-available' })
			return
		}
		setLoading(true)
		try {
			const resp = await azureApiGatewayPost(
				`customer/${customerInfo.customerId}/initiate_bankid`,
				{
					personal_number: customerInfo.sso,
					visible_sign_string: businessCustomer
						? t('kinto.bankid.signing-string.business')
						: t('kinto.bankid.signing-string'),
				},
				{ useToken: true, params: { registration: true } },
			)
			if (resp.data && resp.data.transaction) {
				startBankIdPoll(resp.data.transaction)
				if (onDevice) {
					window.location.href = `bankid:///?autostarttoken=${resp.data.transaction.autostart_token}&redirect=null`
				}
			} else {
				setError({ message: 'kinto.error.something-went-wrong' })
				setLoading(false)
			}
		} catch (error) {
			setLoading(false)
			setError({
				message: get(error, 'response.data.messages')
					? error.response.data.messages
					: 'kinto.error.could-not-inti-bankid',
			})
		}
	}

	return (
		<div {...bem('')}>
			<FormContainer>
				<Button
					aria-label={t('kinto.register.bank-id')}
					variant='contained'
					color='primary'
					onClick={() => initiateBank(true)}
				>
					{t('kinto.register.bank-id')}
				</Button>

				<Button
					aria-label={t('kinto.register.bank-id-device')}
					variant='outlined'
					color='primary'
					onClick={() => initiateBank(false)}
				>
					{t('kinto.register.bank-id-device')}
				</Button>
			</FormContainer>
		</div>
	)
}

export default ConfirmDriversLicenseAndCreditCheckPage
